import { Dispatch, SetStateAction } from 'react';
import ReportReason from '@alltrails/amplitude/enums/ReportReason';
import SuccessToast from '@alltrails/shared/types/successToast';
import logError from '@alltrails/core/utils/logError';
import { EntryPoint, Issue, ReportedUser } from '../types';
import { reportMap, reportList, reportPhoto, reportUserRequest, reportTrailReview, reportFeedComment } from './requests';

const analyticsReportReason = (issue: Issue): ReportReason => {
  switch (issue) {
    case Issue.FakeProfile:
      return 'fake profile';
    case Issue.Spam:
    case Issue.SpamAccount:
      return 'spam';
    case Issue.HarmfulContent:
    case Issue.HarmfulBehavior:
      return 'harmful';
    case Issue.PrivacyIssue:
      return 'privacy';
    default:
      return 'other';
  }
};

async function handleReport(
  entryPoint: EntryPoint,
  reportId: string,
  reportUser: ReportedUser,
  trailReviewId: string | undefined,
  commentId: string | undefined,
  reasonText: string,
  isBlockSelected: boolean,
  setIsErrorToastVisible: (isVisible: boolean) => void,
  setIsLoading: (isLoading: boolean) => void,
  successTextBlock: string,
  successText: string,
  setSuccessToast: Dispatch<SetStateAction<SuccessToast>>,
  closeModal: () => void
): Promise<void> {
  let reportFunction: ((id: string, reason: string, trailReviewId?: string) => Promise<unknown>) | null = null;
  switch (entryPoint) {
    case EntryPoint.Maps:
      reportFunction = reportMap;
      break;
    case EntryPoint.Lists:
      reportFunction = reportList;
      break;
    case EntryPoint.Photos:
      reportFunction = reportPhoto;
      break;
    case EntryPoint.User:
      reportFunction = (_, reason) => reportUserRequest(String(reportUser.id), reason);
      break;
    case EntryPoint.Reviews:
      if (trailReviewId) {
        reportFunction = (id, reason) => reportTrailReview(id, trailReviewId, reason);
      }
      break;
    case EntryPoint.FeedComment:
      if (commentId) {
        reportFunction = (id, reason) => reportFeedComment(id, commentId, reason);
      }
      break;
    default:
      throw new Error(`Invalid entry point: ${entryPoint}`);
  }
  if (!reportFunction) {
    return;
  }

  try {
    await reportFunction(reportId, reasonText);
    setSuccessToast({ isVisible: true, text: isBlockSelected ? successTextBlock : successText });
    closeModal();
  } catch (error) {
    logError(new Error(`Error during reporting: ${error}`));
    setIsErrorToastVisible(true);
  } finally {
    setIsLoading(false);
  }
}

export { handleReport, analyticsReportReason };
